import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Simulated real-time notifications (would connect to WebSocket in production)
  useEffect(() => {
    // Initial notifications
    const initial = [
      { id: 1, type: 'alert', title: 'Payroll Due', message: 'Payroll is due tomorrow for 12 employees', time: new Date(), read: false },
      { id: 2, type: 'info', title: 'Expense Update', message: 'Expenses up 12% vs last month', time: new Date(Date.now() - 3600000), read: false },
      { id: 3, type: 'success', title: 'Payments Received', message: '15 invoices paid this week', time: new Date(Date.now() - 7200000), read: true },
    ];
    setNotifications(initial);
    setUnreadCount(initial.filter(n => !n.read).length);

    // Simulate real-time notifications
    const interval = setInterval(() => {
      const types = ['info', 'alert', 'success'];
      const messages = [
        { title: 'New Payment', message: 'Payment of $1,500 received from Customer A' },
        { title: 'Approval Needed', message: 'Voucher #1234 needs your approval' },
        { title: 'Bank Synced', message: 'Bank feed updated with 5 new transactions' },
        { title: 'AI Alert', message: 'Unusual expense pattern detected' },
      ];
      
      const msg = messages[Math.floor(Math.random() * messages.length)];
      const newNotif = {
        id: Date.now(),
        type: types[Math.floor(Math.random() * types.length)],
        ...msg,
        time: new Date(),
        read: false
      };

      setNotifications(prev => [newNotif, ...prev].slice(0, 20));
      setUnreadCount(prev => prev + 1);
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const markAsRead = useCallback((id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const addNotification = useCallback((notification) => {
    const newNotif = { id: Date.now(), time: new Date(), read: false, ...notification };
    setNotifications(prev => [newNotif, ...prev].slice(0, 20));
    setUnreadCount(prev => prev + 1);
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markAsRead, markAllAsRead, addNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
